import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Tabs,
    Tab,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Alert,
    Snackbar,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Card,
    CardContent,
    Grid,
    Chip,
    Tooltip,
    IconButton as MuiIconButton,
} from '@mui/material';
import { Delete, Edit, PersonAdd, Email, AdminPanelSettings, Person, Visibility, VisibilityOff } from '@mui/icons-material';
import { userApi, netflixAccountApi } from '../../services/api';
import { User, NetflixAccount } from '../../types';
import Navigation from '../common/Navigation';
import { LoadingButton } from '@mui/lab';
import { Login } from '@mui/icons-material';
import { cardStyles, cardContentStyles, cardHeaderStyles, cardActionsStyles } from '../../styles/common';
import { decrypt } from '../../utils/encryption';

interface AdminDashboardProps {
    onLogout: () => void;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
};

const AdminDashboard: React.FC<AdminDashboardProps> = ({ onLogout }) => {
    const [tabValue, setTabValue] = useState(0);
    const [users, setUsers] = useState<User[]>([]);
    const [accounts, setAccounts] = useState<NetflixAccount[]>([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [openAssignDialog, setOpenAssignDialog] = useState(false);
    const [openReassignDialog, setOpenReassignDialog] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState<NetflixAccount | null>(null);
    const [dialogType, setDialogType] = useState<'user' | 'account'>('user');
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        role: 'user',
        userId: '',
        username: '',
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [showPassword, setShowPassword] = useState<{ [key: number]: boolean }>({});
    const [testingLogin, setTestingLogin] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [testResult, setTestResult] = useState({ success: false, message: '' });
    const [decryptedPasswords, setDecryptedPasswords] = useState<{ [key: number]: string }>({});

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            setCurrentUser(JSON.parse(storedUser));
        }
        fetchUsers();
        fetchAccounts();
    }, []);

    const fetchUsers = async () => {
        try {
            const data = await userApi.getAll();
            setUsers(data);
        } catch (error) {
            setError('Failed to fetch users');
        }
    };

    const fetchAccounts = async () => {
        try {
            const data = await netflixAccountApi.getAll();
            setAccounts(data);
        } catch (error) {
            setError('Failed to fetch Netflix accounts');
        }
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleOpenDialog = (type: 'user' | 'account') => {
        setDialogType(type);
        setFormData({
            email: '',
            password: '',
            role: 'user',
            userId: '',
            username: '',
        });
        setOpenDialog(true);
        setError('');
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setError('');
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        // Clear email error when user starts typing
        if (name === 'email') {
            setEmailError('');
            // Validate email format
            if (value) {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(value)) {
                    setEmailError('Please enter a valid email address');
                }
            }
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        setError('');
        try {
            if (dialogType === 'user') {
                await userApi.create({
                    username: formData.username,
                    password: formData.password,
                    role: formData.role,
                });
                setSuccessMessage('User created successfully');
                fetchUsers();
            } else {
                // Validate email format
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(formData.email)) {
                    throw new Error('Please enter a valid email address');
                }
                
                await netflixAccountApi.create({
                    email: formData.email,
                    password: formData.password,
                    userId: formData.userId ? parseInt(formData.userId) : undefined,
                });
                setSuccessMessage('Netflix account created successfully');
                fetchAccounts();
            }
            handleCloseDialog();
        } catch (err: any) {
            setError(err.response?.data?.message || err.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (type: 'user' | 'account', id: number) => {
        try {
            if (type === 'user') {
                await userApi.delete(id);
                setSuccessMessage('User deleted successfully');
                fetchUsers();
            } else {
                await netflixAccountApi.delete(id);
                setSuccessMessage('Netflix account deleted successfully');
                fetchAccounts();
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'An error occurred while deleting');
        }
    };

    const handleOpenAssignDialog = (account: NetflixAccount) => {
        setSelectedAccount(account);
        setOpenAssignDialog(true);
        setError('');
    };

    const handleCloseAssignDialog = () => {
        setOpenAssignDialog(false);
        setSelectedAccount(null);
        setError('');
    };

    const handleAssign = async () => {
        if (!selectedAccount || !formData.userId) return;
        
        setLoading(true);
        setError('');
        try {
            await netflixAccountApi.update(selectedAccount.id, parseInt(formData.userId));
            setSuccessMessage('Netflix account assigned successfully');
            fetchAccounts();
            handleCloseAssignDialog();
        } catch (err: any) {
            setError(err.response?.data?.message || 'An error occurred while assigning the account');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenReassignDialog = (account: NetflixAccount) => {
        setSelectedAccount(account);
        setFormData({ ...formData, userId: account.user_id?.toString() || '' });
        setOpenReassignDialog(true);
        setError('');
    };

    const handleCloseReassignDialog = () => {
        setOpenReassignDialog(false);
        setSelectedAccount(null);
        setError('');
    };

    const handleReassign = async () => {
        if (!selectedAccount || !formData.userId) return;
        
        setLoading(true);
        setError('');
        try {
            await netflixAccountApi.update(selectedAccount.id, parseInt(formData.userId));
            setSuccessMessage('Netflix account reassigned successfully');
            fetchAccounts();
            handleCloseReassignDialog();
        } catch (err: any) {
            setError(err.response?.data?.message || 'An error occurred while reassigning the account');
        } finally {
            setLoading(false);
        }
    };

    const togglePasswordVisibility = async (accountId: number) => {
        if (!showPassword[accountId]) {
            try {
                const account = accounts.find(acc => acc.id === accountId);
                if (account) {
                    const response = await netflixAccountApi.decryptPassword(account.password);
                    setDecryptedPasswords(prev => ({
                        ...prev,
                        [accountId]: response.decryptedPassword
                    }));
                }
            } catch (error) {
                console.error('Failed to decrypt password:', error);
                setError('Failed to decrypt password');
            }
        }
        setShowPassword(prev => ({
            ...prev,
            [accountId]: !prev[accountId]
        }));
    };

    const handleTestLogin = async () => {
        if (!formData.email || !formData.password) {
            setError('Email and password are required');
            return;
        }

        console.log('Testing login for:', formData.email);
        setTestingLogin(true);
        setError('');
        try {
            console.log('Making API call to test login...');
            const response = await netflixAccountApi.testLogin({
                email: formData.email,
                password: formData.password
            });
            console.log('Test login response:', response);
            if (response.success) {
                setSuccessMessage(response.message || 'Login test successful!');
            } else {
                setError(response.message || 'Login test failed. Please check the credentials.');
            }
        } catch (err: any) {
            console.error('Test login error:', err);
            setError(err.response?.data?.message || 'Login test failed. Please check the credentials.');
        } finally {
            setTestingLogin(false);
        }
    };

    if (!currentUser) {
        return null;
    }

    return (
        <>
            <Navigation user={currentUser} onLogout={onLogout} />
            <Container>
                <Paper sx={{ width: '100%', mt: 3 }}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={tabValue} onChange={handleTabChange}>
                            <Tab label="Users" />
                            <Tab label="Netflix Accounts" />
                        </Tabs>
                    </Box>

                    <TabPanel value={tabValue} index={0}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h6" color="primary">Manage Users</Typography>
                            <Button variant="contained" onClick={() => handleOpenDialog('user')}>
                                Add User
                            </Button>
                        </Box>
                        <Grid container spacing={2}>
                            {users.map((user) => (
                                <Grid item xs={12} sm={6} md={4} key={user.id}>
                                    <Card elevation={3}>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                {user.role === 'admin' ? (
                                                    <AdminPanelSettings color="primary" sx={{ mr: 1 }} />
                                                ) : (
                                                    <Person color="primary" sx={{ mr: 1 }} />
                                                )}
                                                <Typography variant="h6" component="div">
                                                    {user.username}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                                                <Chip
                                                    label={user.role}
                                                    color={user.role === 'admin' ? 'error' : 'primary'}
                                                    size="small"
                                                />
                                                {user.role !== 'admin' && (
                                                    <IconButton
                                                        onClick={() => handleDelete('user', user.id)}
                                                        size="small"
                                                    >
                                                        <Delete />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </TabPanel>

                    <TabPanel value={tabValue} index={1}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h6" color="primary">Manage Netflix Accounts</Typography>
                            <Button variant="contained" onClick={() => handleOpenDialog('account')}>
                                Add Netflix Account
                            </Button>
                        </Box>
                        <Grid container spacing={2}>
                            {accounts.map((account) => (
                                <Grid item xs={12} sm={6} md={4} key={account.id}>
                                    <Card elevation={3}>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                <Email color="primary" sx={{ mr: 1 }} />
                                                <Typography variant="h6" component="div">
                                                    {account.email}
                                                </Typography>
                                            </Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                                Password: {showPassword[account.id] ? decryptedPasswords[account.id] || '••••••••' : '••••••••'}
                                                <Button
                                                    size="small"
                                                    onClick={() => togglePasswordVisibility(account.id)}
                                                    sx={{ ml: 1 }}
                                                    startIcon={showPassword[account.id] ? <VisibilityOff /> : <Visibility />}
                                                >
                                                    {showPassword[account.id] ? 'Hide Password' : 'Show Password'}
                                                </Button>
                                            </Typography>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                                                <Chip
                                                    label={account.user_username ? `Assigned to: ${account.user_username}` : 'Unassigned'}
                                                    color={account.user_username ? 'success' : 'warning'}
                                                    size="small"
                                                />
                                                <Box>
                                                    {!account.user_id ? (
                                                        <Tooltip title="Assign to user">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleOpenAssignDialog(account)}
                                                                sx={{ mr: 1 }}
                                                            >
                                                                <PersonAdd />
                                                            </IconButton>
                                                        </Tooltip>
                                                    ) : (
                                                        <Tooltip title="Reassign to different user">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleOpenReassignDialog(account)}
                                                                sx={{ mr: 1 }}
                                                            >
                                                                <Edit />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    <Tooltip title="Delete account">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDelete('account', account.id)}
                                                        >
                                                            <Delete />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </TabPanel>
                </Paper>

                <Dialog open={openDialog} onClose={handleCloseDialog}>
                    <DialogTitle>
                        {dialogType === 'user' ? 'Add New User' : 'Add Netflix Account'}
                    </DialogTitle>
                    <DialogContent>
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}
                        {dialogType === 'user' ? (
                            <>
                                <TextField
                                    autoFocus
                                    margin="dense"
                                    name="username"
                                    label="Username"
                                    type="text"
                                    fullWidth
                                    value={formData.username}
                                    onChange={handleInputChange}
                                />
                                <TextField
                                    margin="dense"
                                    name="password"
                                    label="Password"
                                    type="password"
                                    fullWidth
                                    value={formData.password}
                                    onChange={handleInputChange}
                                />
                                <FormControl fullWidth margin="dense">
                                    <InputLabel>Role</InputLabel>
                                    <Select
                                        name="role"
                                        value={formData.role}
                                        onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                                    >
                                        <MenuItem value="user">User</MenuItem>
                                        <MenuItem value="admin">Admin</MenuItem>
                                    </Select>
                                </FormControl>
                            </>
                        ) : (
                            <>
                                <TextField
                                    autoFocus
                                    margin="dense"
                                    name="email"
                                    label="Email Address"
                                    type="email"
                                    fullWidth
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    error={!!emailError}
                                    helperText={emailError}
                                />
                                <TextField
                                    margin="dense"
                                    name="password"
                                    label="Password"
                                    type="password"
                                    fullWidth
                                    value={formData.password}
                                    onChange={handleInputChange}
                                />
                                <Box sx={{ display: 'flex', gap: 1, mt: 2, mb: 1 }}>
                                    <LoadingButton
                                        variant="outlined"
                                        startIcon={<Login />}
                                        loading={testingLogin}
                                        onClick={handleTestLogin}
                                        disabled={loading || !formData.email || !formData.password || !!emailError}
                                        sx={{ flexGrow: 1 }}
                                    >
                                        Test Login
                                    </LoadingButton>
                                </Box>
                                <FormControl fullWidth margin="dense">
                                    <InputLabel>Assign to User (Optional)</InputLabel>
                                    <Select
                                        name="userId"
                                        value={formData.userId}
                                        onChange={(e) => setFormData(prev => ({ ...prev, userId: e.target.value }))}
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        {users.map((user) => (
                                            <MenuItem key={user.id} value={user.id}>
                                                {user.username}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>Cancel</Button>
                        <LoadingButton 
                            onClick={handleSubmit} 
                            loading={loading}
                            disabled={dialogType === 'account' && !!emailError}
                        >
                            Add
                        </LoadingButton>
                    </DialogActions>
                </Dialog>

                <Dialog open={openAssignDialog} onClose={handleCloseAssignDialog}>
                    <DialogTitle>
                        Assign Netflix Account
                    </DialogTitle>
                    <DialogContent>
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}
                        <FormControl fullWidth sx={{ mt: 2 }}>
                            <InputLabel>Select User</InputLabel>
                            <Select
                                value={formData.userId}
                                onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
                                disabled={loading}
                            >
                                {users.map((user) => (
                                    <MenuItem key={user.id} value={user.id}>
                                        {user.username}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseAssignDialog} disabled={loading}>
                            Cancel
                        </Button>
                        <Button onClick={handleAssign} disabled={loading || !formData.userId}>
                            Assign
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={openReassignDialog} onClose={handleCloseReassignDialog}>
                    <DialogTitle>
                        Reassign Netflix Account
                    </DialogTitle>
                    <DialogContent>
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Currently assigned to: {selectedAccount?.user_username || 'None'}
                        </Typography>
                        <FormControl fullWidth>
                            <InputLabel>Select New User</InputLabel>
                            <Select
                                value={formData.userId}
                                onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
                                disabled={loading}
                            >
                                {users.map((user) => (
                                    <MenuItem key={user.id} value={user.id}>
                                        {user.username}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseReassignDialog} disabled={loading}>
                            Cancel
                        </Button>
                        <Button onClick={handleReassign} disabled={loading || !formData.userId}>
                            Reassign
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar
                    open={!!successMessage}
                    autoHideDuration={6000}
                    onClose={() => setSuccessMessage('')}
                    message={successMessage}
                />
            </Container>
        </>
    );
};

export default AdminDashboard; 